import React from 'react';
import { hueOf } from './AccountMenu';

/**
 * Dimensional robot avatars for agents/FMAs — the one deliberately 3D-looking
 * element in the app. A gallery of original archetypes (each EVOKES a classic
 * robot design language without copying any franchise character): pick per FMA,
 * stored as `robot:<key>` in the agent's avatar_url. Hue stays stable per agent
 * name so every robot keeps its own color.
 */

export const ROBOT_VARIANTS: { key: string; label: string }[] = [
  { key: 'classic', label: 'Classic' },
  { key: 'astro', label: 'Astro droid' },
  { key: 'slab', label: 'Monolith' },
  { key: 'android', label: 'Android' },
  { key: 'retro', label: 'Retro tin' },
  { key: 'orb', label: 'Orb' },
  { key: 'crt', label: 'Terminal' },
  { key: 'bug', label: 'Bug' },
  { key: 'dish', label: 'Satellite' },
  { key: 'periscope', label: 'Periscope' },
  { key: 'tank', label: 'Tank' },
  { key: 'saucer', label: 'Saucer' },
  { key: 'felinoid', label: 'Felinoid' },
  { key: 'visor', label: 'Visor' },
  { key: 'rover', label: 'Rover' },
  { key: 'crown', label: 'Crown' },
];

/** 'robot:astro' → 'astro'; anything else (real image URL / empty) → undefined. */
export function robotVariantOf(avatarUrl?: string | null): string | undefined {
  if (avatarUrl && avatarUrl.startsWith('robot:')) {
    const k = avatarUrl.slice(6);
    return ROBOT_VARIANTS.some((v) => v.key === k) ? k : undefined;
  }
  return undefined;
}

/** Renders whatever the agent's avatar_url says: robot variant, real image, or default robot. */
export function AgentFace({ name, avatarUrl, size = 24 }: { name?: string | null; avatarUrl?: string | null; size?: number }) {
  const v = robotVariantOf(avatarUrl);
  if (avatarUrl && !v && !avatarUrl.startsWith('robot:')) {
    return <img src={avatarUrl} width={size} height={size} style={{ borderRadius: '30%', objectFit: 'cover', display: 'block' }} alt="" />;
  }
  return <AgentAvatar name={name} variant={v} size={size} />;
}

export function AgentAvatar({ name, variant = 'classic', size = 28 }: { name?: string | null; variant?: string; size?: number }) {
  const h = hueOf(name);
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, '');
  const head = `hd${uid}`;
  const face = `fc${uid}`;
  const shine = `sh${uid}`;
  // Shared shading palette
  const hi = `hsl(${h}, 85%, 68%)`;
  const mid = `hsl(${h}, 75%, 50%)`;
  const lo = `hsl(${h}, 80%, 34%)`;
  const dk1 = `hsl(${h}, 45%, 20%)`;
  const dk2 = `hsl(${h}, 50%, 12%)`;
  const glow = `hsl(${h}, 100%, 74%)`;
  const trim = `hsl(${h}, 72%, 40%)`;

  const defs = (
    <defs>
      <linearGradient id={head} x1="0" y1="0" x2="0.4" y2="1">
        <stop offset="0" stopColor={hi} /><stop offset="0.55" stopColor={mid} /><stop offset="1" stopColor={lo} />
      </linearGradient>
      <linearGradient id={face} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={dk1} /><stop offset="1" stopColor={dk2} />
      </linearGradient>
      <radialGradient id={shine} cx="0.32" cy="0.18" r="0.7">
        <stop offset="0" stopColor="rgba(255,255,255,0.7)" /><stop offset="0.5" stopColor="rgba(255,255,255,0)" />
      </radialGradient>
    </defs>
  );
  const eye = (cx: number, cy: number, r = 2) => (
    <>
      <circle cx={cx} cy={cy} r={r} fill={glow} />
      <circle cx={cx - r * 0.25} cy={cy - r * 0.3} r={r * 0.35} fill="#fff" opacity="0.9" />
    </>
  );
  const headRect = (x: number, y: number, w: number, hh: number, rx: number) => (
    <>
      <rect x={x} y={y} width={w} height={hh} rx={rx} fill={`url(#${head})`} />
      <rect x={x} y={y} width={w} height={hh} rx={rx} fill={`url(#${shine})`} />
    </>
  );

  let body: React.ReactNode;
  switch (variant) {
    case 'astro': // dome head, single lens, panel seams
      body = (
        <>
          <path d={`M4 20 A12 12 0 0 1 28 20 L28 24 A2 2 0 0 1 26 26 L6 26 A2 2 0 0 1 4 24 Z`} fill={`url(#${head})`} />
          <path d={`M4 20 A12 12 0 0 1 28 20 L28 24 A2 2 0 0 1 26 26 L6 26 A2 2 0 0 1 4 24 Z`} fill={`url(#${shine})`} />
          <rect x="4" y="19.4" width="24" height="1.4" fill={trim} opacity="0.8" />
          <circle cx="16" cy="14.5" r="3.4" fill={`url(#${face})`} />
          {eye(16, 14.5, 2)}
          <rect x="8" y="21.6" width="4.6" height="2.6" rx="1" fill={dk1} />
          <rect x="19.4" y="21.6" width="4.6" height="2.6" rx="1" fill={dk1} />
        </>
      );
      break;
    case 'slab': // tall segmented monolith
      body = (
        <>
          {headRect(9, 4, 14, 24, 2.5)}
          <rect x="9" y="11.2" width="14" height="1.2" fill={lo} opacity="0.85" />
          <rect x="9" y="19.6" width="14" height="1.2" fill={lo} opacity="0.85" />
          <rect x="12" y="6.4" width="8" height="3" rx="1.2" fill={`url(#${face})`} />
          {eye(16, 7.9, 1.3)}
        </>
      );
      break;
    case 'android': // sleek helmet, wide glowing visor
      body = (
        <>
          <path d="M6 13 A10 10 0 0 1 26 13 L26 22 A6 6 0 0 1 20 28 L12 28 A6 6 0 0 1 6 22 Z" fill={`url(#${head})`} />
          <path d="M6 13 A10 10 0 0 1 26 13 L26 22 A6 6 0 0 1 20 28 L12 28 A6 6 0 0 1 6 22 Z" fill={`url(#${shine})`} />
          <rect x="8.4" y="14.4" width="15.2" height="5" rx="2.5" fill={`url(#${face})`} />
          <rect x="10" y="16" width="12" height="1.8" rx="0.9" fill={glow} />
          <rect x="13.4" y="23.4" width="5.2" height="1.6" rx="0.8" fill={dk1} />
        </>
      );
      break;
    case 'retro': // square tin head, bolt ears, grille mouth
      body = (
        <>
          <rect x="14.8" y="3.2" width="2.4" height="3.4" fill={trim} />
          <circle cx="16" cy="3" r="1.7" fill={glow} />
          <rect x="3.4" y="14" width="3" height="6" rx="1" fill={trim} />
          <rect x="25.6" y="14" width="3" height="6" rx="1" fill={trim} />
          {headRect(6, 7, 20, 20, 2)}
          <rect x="9" y="12" width="5.4" height="4.4" rx="1" fill={`url(#${face})`} />
          <rect x="17.6" y="12" width="5.4" height="4.4" rx="1" fill={`url(#${face})`} />
          {eye(11.7, 14.2, 1.5)}{eye(20.3, 14.2, 1.5)}
          <rect x="10" y="20.4" width="12" height="3" rx="1" fill={`url(#${face})`} />
          <rect x="12" y="20.4" width="1.2" height="3" fill={mid} /><rect x="15.4" y="20.4" width="1.2" height="3" fill={mid} /><rect x="18.8" y="20.4" width="1.2" height="3" fill={mid} />
        </>
      );
      break;
    case 'orb': // floating sphere, one big eye
      body = (
        <>
          <circle cx="16" cy="16" r="12" fill={`url(#${head})`} />
          <circle cx="16" cy="16" r="12" fill={`url(#${shine})`} />
          <circle cx="16" cy="16" r="6" fill={`url(#${face})`} />
          {eye(16, 16, 3)}
        </>
      );
      break;
    case 'crt': // monitor head on a neck
      body = (
        <>
          <rect x="13.4" y="24" width="5.2" height="4" fill={trim} />
          {headRect(4.5, 6, 23, 18, 3)}
          <rect x="7" y="8.5" width="18" height="13" rx="2" fill={`url(#${face})`} />
          <rect x="10" y="12" width="3.4" height="3.4" rx="0.6" fill={glow} />
          <rect x="18.6" y="12" width="3.4" height="3.4" rx="0.6" fill={glow} />
          <rect x="12" y="17.6" width="8" height="1.6" rx="0.8" fill={glow} opacity="0.75" />
        </>
      );
      break;
    case 'bug': // antennae + compound eyes
      body = (
        <>
          <path d="M10 7 Q8 3.4 5.4 3.4" stroke={trim} strokeWidth="1.7" fill="none" strokeLinecap="round" />
          <path d="M22 7 Q24 3.4 26.6 3.4" stroke={trim} strokeWidth="1.7" fill="none" strokeLinecap="round" />
          <circle cx="5.2" cy="3.4" r="1.6" fill={glow} /><circle cx="26.8" cy="3.4" r="1.6" fill={glow} />
          <ellipse cx="16" cy="18" rx="11.5" ry="10" fill={`url(#${head})`} />
          <ellipse cx="16" cy="18" rx="11.5" ry="10" fill={`url(#${shine})`} />
          <ellipse cx="11.4" cy="17" rx="3.4" ry="4.2" fill={`url(#${face})`} />
          <ellipse cx="20.6" cy="17" rx="3.4" ry="4.2" fill={`url(#${face})`} />
          {eye(11.4, 16.6, 1.7)}{eye(20.6, 16.6, 1.7)}
        </>
      );
      break;
    case 'dish': // radar dish on head
      body = (
        <>
          <path d="M9 6.5 A8 4.5 0 0 1 23 6.5 L16 9 Z" fill={trim} />
          <circle cx="16" cy="5.4" r="1.4" fill={glow} />
          {headRect(6.5, 9, 19, 18.5, 4)}
          <rect x="9.4" y="13.4" width="13.2" height="7.6" rx="3" fill={`url(#${face})`} />
          {eye(12.6, 17.2, 1.8)}{eye(19.4, 17.2, 1.8)}
        </>
      );
      break;
    case 'periscope': // eye box on a stalk
      body = (
        <>
          {headRect(8, 17, 16, 11, 3)}
          <rect x="14.6" y="10" width="2.8" height="8" fill={trim} />
          {headRect(10.5, 3.5, 13, 7.5, 2.5)}
          <rect x="12.6" y="5.3" width="5.8" height="3.8" rx="1.6" fill={`url(#${face})`} />
          {eye(15.5, 7.2, 1.6)}
          <rect x="11" y="21" width="10" height="2.4" rx="1.2" fill={`url(#${face})`} />
        </>
      );
      break;
    case 'tank': // wide low head, eye slit
      body = (
        <>
          {headRect(3.5, 10, 25, 15, 4)}
          <rect x="6.5" y="7" width="19" height="5" rx="2.4" fill={trim} />
          <rect x="7" y="15" width="18" height="4.6" rx="2.3" fill={`url(#${face})`} />
          <rect x="9.4" y="16.6" width="13.2" height="1.6" rx="0.8" fill={glow} />
          <circle cx="8" cy="21.8" r="1.1" fill={lo} /><circle cx="16" cy="21.8" r="1.1" fill={lo} /><circle cx="24" cy="21.8" r="1.1" fill={lo} />
        </>
      );
      break;
    case 'saucer': // flying-saucer head with dome
      body = (
        <>
          <path d="M9.5 13 A7 6 0 0 1 22.5 13 Z" fill={`url(#${head})`} />
          <ellipse cx="16" cy="15.5" rx="13" ry="4.6" fill={`url(#${head})`} />
          <ellipse cx="16" cy="15.5" rx="13" ry="4.6" fill={`url(#${shine})`} />
          <ellipse cx="16" cy="14.4" rx="5.4" ry="2" fill={`url(#${face})`} />
          {eye(16, 14.4, 1.5)}
          <circle cx="8" cy="21.5" r="1.3" fill={glow} opacity="0.85" /><circle cx="16" cy="23" r="1.3" fill={glow} opacity="0.85" /><circle cx="24" cy="21.5" r="1.3" fill={glow} opacity="0.85" />
        </>
      );
      break;
    case 'felinoid': // ear triangles, slanted eyes
      body = (
        <>
          <path d="M7 12 L9.5 4 L14 9.5 Z" fill={mid} /><path d="M25 12 L22.5 4 L18 9.5 Z" fill={mid} />
          {headRect(6, 9, 20, 18, 6)}
          <path d="M10 15.6 L15 17.4 L10 19.2 Z" fill={glow} />
          <path d="M22 15.6 L17 17.4 L22 19.2 Z" fill={glow} />
          <path d="M14.6 22.6 L16 24 L17.4 22.6" stroke={dk1} strokeWidth="1.4" fill="none" strokeLinecap="round" />
        </>
      );
      break;
    case 'visor': // narrow head, full-width visor band
      body = (
        <>
          {headRect(7, 6.5, 18, 21, 5)}
          <rect x="4.5" y="12.6" width="23" height="6.2" rx="3.1" fill={`url(#${face})`} />
          <rect x="7" y="14.6" width="18" height="2.2" rx="1.1" fill={glow} opacity="0.9" />
        </>
      );
      break;
    case 'rover': // twin-eye head on wheels
      body = (
        <>
          {headRect(5.5, 7, 21, 15, 3.5)}
          <rect x="8.4" y="10.4" width="15.2" height="8" rx="2.6" fill={`url(#${face})`} />
          {eye(12.6, 14.4, 2)}{eye(19.4, 14.4, 2)}
          <circle cx="10" cy="25" r="3" fill={dk1} /><circle cx="22" cy="25" r="3" fill={dk1} />
          <circle cx="10" cy="25" r="1.2" fill={mid} /><circle cx="22" cy="25" r="1.2" fill={mid} />
        </>
      );
      break;
    case 'crown': // antenna array
      body = (
        <>
          <rect x="9.2" y="3.6" width="1.6" height="5" rx="0.8" fill={trim} /><circle cx="10" cy="3.4" r="1.4" fill={glow} />
          <rect x="15.2" y="2.2" width="1.6" height="6.4" rx="0.8" fill={trim} /><circle cx="16" cy="2" r="1.4" fill={glow} />
          <rect x="21.2" y="3.6" width="1.6" height="5" rx="0.8" fill={trim} /><circle cx="22" cy="3.4" r="1.4" fill={glow} />
          {headRect(6, 8.5, 20, 19, 5)}
          <rect x="9.4" y="13.4" width="13.2" height="8" rx="3.4" fill={`url(#${face})`} />
          {eye(12.8, 17.4, 1.8)}{eye(19.2, 17.4, 1.8)}
        </>
      );
      break;
    default: // classic
      body = (
        <>
          <rect x="15.1" y="3.4" width="1.8" height="4" rx="0.9" fill={trim} />
          <circle cx="16" cy="3.2" r="2" fill={`hsl(${h}, 95%, 72%)`} />
          <circle cx="16" cy="3.2" r="0.9" fill="#fff" opacity="0.85" />
          <rect x="2.6" y="13.5" width="2.6" height="7" rx="1.3" fill={trim} />
          <rect x="26.8" y="13.5" width="2.6" height="7" rx="1.3" fill={trim} />
          {headRect(5, 7.5, 22, 20, 6.5)}
          <rect x="8.4" y="13" width="15.2" height="9.6" rx="4" fill={`url(#${face})`} />
          {eye(12.7, 17.8, 2)}{eye(19.3, 17.8, 2)}
        </>
      );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden focusable="false" style={{ display: 'block' }}>
      {defs}
      {body}
    </svg>
  );
}
